import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { BookOpen, Heart, Users, Sparkles, FileText, Send, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASES = [
  {
    icon: BookOpen,
    title: 'Configura el libro',
    desc: 'Cuéntanos quién es la persona protagonista, la ocasión y un poco de su historia. Cuanto más nos cuentes, mejor saldrá el libro.',
  },
  {
    icon: Users,
    title: 'Invita a familia y amigos',
    desc: 'Comparte un enlace privado con quienes quieras que aporten. Cada uno responde un cuestionario sencillo con sus recuerdos, anécdotas y mensajes.',
  },
  {
    icon: Sparkles,
    title: 'La IA escribe el libro',
    desc: 'Nuestra IA narrativa une todas las aportaciones en una biografía coral y emotiva. Tú revisas y eliges los toques finales (título, tono, etc.).',
  },
  {
    icon: FileText,
    title: 'Diseño y maquetación',
    desc: 'Aplicamos tipografías cuidadas, capítulos por etapas y una portada personalizada. El libro queda listo para imprenta.',
  },
  {
    icon: Send,
    title: 'Imprenta y envío',
    desc: 'Imprimimos en tapa dura con calidad editorial y lo enviamos a la dirección que indiques. Listo para regalar.',
  },
];

export default function WelcomeNewUserModal({ open, onClose, userName }) {
  const [closing, setClosing] = useState(false);

  const handleClose = async () => {
    setClosing(true);
    try {
      await axios.post(`${API_URL}/api/auth/welcome-shown`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setClosing(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="welcome-new-user-modal">
        <DialogTitle className="sr-only">Bienvenida a A Gift of Memories</DialogTitle>
        <DialogDescription className="sr-only">Guía rápida de primeros pasos para crear tu libro de recuerdos.</DialogDescription>
        {/* Header banner */}
        <div className="relative bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 p-6 sm:p-8 text-white">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-white/80 hover:text-white"
            aria-label="Cerrar"
            data-testid="welcome-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-4">
            <Heart className="w-6 h-6" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold leading-tight">
            ¡Bienvenido/a, {userName?.split(' ')[0] || 'a A Gift of Memories'}!
          </h2>
          <p className="text-white/90 text-sm sm:text-base mt-2 leading-relaxed">
            Gracias por confiar en nosotros para regalar algo único y emotivo.
            Estamos encantados de acompañarte en este proyecto.
          </p>
        </div>

        {/* Mission */}
        <div className="px-6 sm:px-8 pt-6 pb-2">
          <h3 className="font-heading font-semibold text-base mb-2">¿En qué consiste el proyecto?</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            <strong className="text-foreground">A Gift of Memories</strong> convierte los recuerdos
            de quienes más quieren a una persona en un <strong>libro biográfico físico</strong>, escrito
            con calidez por nuestra IA narrativa, ilustrado con sus fotos y maquetado con calidad
            editorial. Es un regalo único: cuenta su vida con las voces de quienes la han acompañado.
          </p>
        </div>

        {/* Phases */}
        <div className="px-6 sm:px-8 py-4">
          <h3 className="font-heading font-semibold text-base mb-3">Cómo va a ser el proceso</h3>
          <ol className="space-y-3">
            {PHASES.map((phase, idx) => {
              const Icon = phase.icon;
              return (
                <li key={idx} className="flex gap-3 items-start" data-testid={`welcome-phase-${idx}`}>
                  <div className="flex-shrink-0 w-9 h-9 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-semibold text-sm">
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      <Icon className="w-3.5 h-3.5 text-purple-600" />
                      {phase.title}
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">
                      {phase.desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Footer reassurance + CTA */}
        <div className="px-6 sm:px-8 pb-6 pt-2 space-y-3">
          <div className="rounded-md bg-purple-50 border border-purple-100 p-3">
            <p className="text-xs text-purple-900 leading-relaxed">
              💡 <strong>No tienes que tener todo claro desde el inicio.</strong> Puedes empezar
              con poca información, ir invitando a colaboradores poco a poco y avanzar a tu ritmo.
              Estamos aquí para ayudarte cuando lo necesites.
            </p>
          </div>
          <Button
            onClick={handleClose}
            disabled={closing}
            className="w-full gap-2 bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="welcome-cta-btn"
          >
            Empezar mi primer libro
            <Sparkles className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
